import { ExternalToast, toast } from 'sonner';

import { Button } from '@/components/ui/button';

import { Check } from '@/utils/lucide';
import { CheckCircle } from '@/utils/lucide';
import { X } from '@/utils/lucide';
import { AlertOctagon } from '@/utils/lucide';
import { Info } from '@/utils/lucide';
import { AlertTriangle } from '@/utils/lucide';

import { setOverlay } from '@/redux/layoutSlice';
import store from '@/redux/store';

import getIcon, { hasIcon } from '../getIcon';
import { cn } from '../lib/utils';
import { z_classes } from '../z';

interface ToastProps
{
	variant: 'success' | 'outline' | 'neutral' | 'error' | 'indicator' | 'accent';

	title: string;
	description?: string;
	footer?: string;
	icon?: string;

	className?: string;
	titleClassName?: string;
	descriptionClassName?: string;
	footerClassName?: string;
	options?: ExternalToast;

	onClick?: () => void;
	buttonText?: string;
}

const showToast = ({
	variant,
	title,
	description,
	footer,
	icon,
	className,
	titleClassName,
	descriptionClassName,
	footerClassName,
	options,
	onClick,
	buttonText
}: ToastProps) =>
{
	let Icon = Check;
	if (icon && hasIcon(icon)) Icon = getIcon(icon);
	else
	{
		switch (variant)
		{
			case 'success':
				Icon = CheckCircle;
				break;
			default:
			case 'outline':
			case 'neutral':
				Icon = Info;
				break;
			case 'error':
				Icon = AlertOctagon;
				break;
			case 'indicator':
				Icon = AlertTriangle;
				break;
		}
	}

	let variantClass = '';
	switch (variant)
	{
		case 'success':
			variantClass = '  bg-maven-primary-50 border border-maven-primary-200 text-maven-primary-500';
			break;
		default:
		case 'outline':
			variantClass = 'bg-white border border-gray-200';
			break;
		case 'neutral':
			variantClass = 'bg-gray-50 border border-gray-200 text-gray-500';
			break;
		case 'error':
			variantClass = 'bg-maven-error-50 border border-maven-error-200 text-maven-error-500';
			break;
		case 'indicator':
			variantClass = 'bg-maven-indicator-50 border border-maven-indicator-200 text-maven-indicator-500';
			break;
		case 'accent':
			variantClass = 'bg-maven-accent-50 border border-maven-accent-200 text-maven-accent-500';
			break;
	}

	return toast.custom(
		t => (
			<div
				className={cn('flex flex-row rounded-md p-2 pr-1 shadow-lg border !border-l-[6px]', z_classes.overlay[1], variantClass)}
				onClick={() =>
				{
					onClick?.();
					if (onClick)
					{
						store.dispatch(setOverlay(null));
					}
				}}
			>
				<div className={cn('p-2 h-full', description ? 'pt-[14px]' : 'pt-[11px]')}>
					<span className='text-icon'>
						<Icon />
					</span>
				</div>

				<div className='flex flex-col min-w-[20ch] p-2'>
					<div className='flex flex-row w-full justify-between items-center gap-x-4'>
						<p className={cn('flex flex-row items-center gap-2 text-lg font-semibold text-gray-900')}>{title}</p>
					</div>
					{description && <p className={cn('text-gray-900', descriptionClassName)}>{description}</p>}
					{footer && <p className={cn('text-sm italic text-gray-900', footerClassName)}>{footer}</p>}
				</div>

				{onClick && (
					<div className='min-h-full h-full items-center justify-center'>
						<Button
							variant='outline'
							className={cn('my-[50%] !bg-white font-semibold', variantClass)}
							onClick={e =>
							{
								onClick?.();
								store.dispatch(setOverlay(null));
								e.stopPropagation();
								e.preventDefault();
							}}
						>
							{buttonText ?? 'View'}
						</Button>
					</div>
				)}

				<div className='p-2 pt-0 h-full'>
					<p
						className='text-icon-sm cursor-pointer'
						onClick={() =>
						{
							toast.dismiss(t);
						}}
					>
						<X />
					</p>
				</div>
			</div>
		),
		options
	);
};

export default showToast;
